import useDateHelperUtils from 'src/composables/dateHelperUtils';
import useRateHelperUtils from 'src/composables/rateHelperUtils.ts';
import useUtils from 'src/composables/utils';
import {
  PreCheckInReservationEditDTO,
  CreditCardDTO,
  PreCheckInReservationItemViewDTO,
  PolicyDTO,
  AccountDTO,
  PreCheckInReservationItemEditDTO,
  SavePreCheckInRequestDTO,
  PreCheckInConfigurationDTO,
  ApiDropdown,
  PreCheckInReservationItemServiceDTO,
  RateDTO,
  VisitorLevyDTO,
} from 'src/services/api/data-contracts';
import {
  ReservationItemService,
  Guest,
  Booker,
  IReservationItem,
  IVatSettings,
  Rate,
} from 'src/types/common';

const dateHelperUtils = useDateHelperUtils();
const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();

export enum PreCheckinProcessStep {
  None = 0,
  Start = 1,
  Booker = 2,
  Room = 3,
  Guest = 4,
  Extras = 5,
  PaymentDetails = 6,
  Summary = 7,
  Complete = 8,
}

export class InvalidPreCheckInResponse {
  constructor() {
    this.BadgeColour;
  }
  BadgeColour: string | null;
  BadgeMessage: string | null;
  Message: string | null;
}

export class PreCheckInReservation {
  constructor(
    data: PreCheckInReservationEditDTO,
    config: PreCheckInConfigurationDTO,
    vatSettings: IVatSettings,
  ) {
    this.PropertyId = data.PropertyId;
    this.ReferenceNumber = data.ReferenceNumber;
    this.IsReservation = data.IsReservation;
    this.EntityId = data.EntityId;
    this.SpecialRequests = data.SpecialRequests;

    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;

    this.CreditCards = data.CreditCards;
    this.Booker =
      data.Booker != null
        ? new Booker(data.Booker, config.BookerConfiguration)
        : null;
    this.Company = data.Company;

    this.ReservationItems = data.ReservationItems.map((g, index) => {
      const ri = new PreCheckInReservationItem(g, config, vatSettings);
      ri.roomNumber = index + 1;
      return ri;
    });

    this.visitorLevy = data.VisitorLevy;
  }

  PropertyId: number;
  ReferenceNumber: string;
  IsReservation: boolean;
  EntityId: number;
  SpecialRequests: string;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  CreditCards: CreditCardDTO[];
  Booker?: Booker;
  Company?: AccountDTO;
  ReservationItems: PreCheckInReservationItem[];

  visitorLevy: VisitorLevyDTO | null;

  get welcomeName(): string {
    if (this.IsReservation) {
      const b = this.Booker;
      if (b != null) {
        return b.FirstName;
      } else {
        return 'Guest';
      }
    } else {
      const firstRes = this.ReservationItems[0];
      if (firstRes.Guests.length > 0) {
        const leadGuest = firstRes.Guests[0];
        return leadGuest.FirstName;
      }
    }
    return '';
  }

  get leadGuestFullName(): string {
    if (this.IsReservation) {
      const b = this.Booker;
      if (b != null) {
        return b.FirstName + ' ' + b.LastName;
      } else if (this.Company != null) {
        return this.Company.Name;
      }
    } else {
      const firstRes = this.ReservationItems[0];
      if (firstRes.Guests.length > 0) {
        const leadGuest = firstRes.Guests[0];
        return leadGuest.FirstName + ' ' + leadGuest.LastName;
      }
    }
    return '';
  }

  updateGuestsWithBooker() {
    const bookerGuests = this.ReservationItems.flatMap(
      (reservation) => reservation.Guests,
    ).filter((guest) => guest.IndividualId === this.Booker.IndividualId);

    if (bookerGuests.length > 0) {
      bookerGuests.forEach((bg) => {
        bg.FirstName = this.Booker.FirstName;
        bg.LastName = this.Booker.LastName;
        bg.Email = this.Booker.Email;
        bg.Phone = this.Booker.Phone;

        bg.haveReviewedGuest = true;
      });
    }
  }

  get TotalAccommodationPrice(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalAccommodationPrice;
    }, 0);
  }

  get TotalServicesPrice(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalServicesPrice;
    }, 0);
  }

  get TotalVatAmount(): number {
    return this.ReservationItems.reduce((t, y) => {
      return t + y.TotalAccommodationVat + y.TotalServicesVat;
    }, 0);
  }

  toDTO(hash: string): SavePreCheckInRequestDTO {
    return {
      PropertyId: this.PropertyId,
      ReferenceNumber: this.ReferenceNumber,
      IsReservation: this.IsReservation,
      EntityId: this.EntityId,
      Code: hash,
      SpecialRequests: this.SpecialRequests,
      Booker: this.Booker != null ? this.Booker.toDTO() : null,
      ReservationItems: this.ReservationItems.map((ri) => {
        return ri.toDTO();
      }),
    } as SavePreCheckInRequestDTO;
  }
}

export class PreCheckInReservationItem implements IReservationItem {
  constructor(
    data: PreCheckInReservationItemViewDTO,
    config: PreCheckInConfigurationDTO,
    vatSettings: IVatSettings,
  ) {
    this.ReservationItemId = data.ReservationItemId;
    this.roomId = data.ReservationItemId.toString();
    this.DateOfArrival = data.DateOfArrival;
    this.DateOfDeparture = data.DateOfDeparture;
    this.RoomTypeId = data.RoomTypeId;
    this.RoomType = data.RoomType;
    this.RatePlanId = data.RatePlanId;
    this.RatePlan = data.RatePlan;
    this.Policy = data.Policy;
    this.RoomConfiguration = data.RoomConfiguration;
    this.RoomConfigurationIsRequired = config.ShowRoomConfigurationQuestion;
    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;

    this.VatSettings = vatSettings;

    this.CreditCards = data.CreditCards;
    this.Guests = data.Guests.map((g) => {
      return new Guest(g, config.GuestConfiguration);
    });
    this.Services = data.Services.map((g) => {
      return new ReservationItemService(g);
    });

    this.OriginalTotalAccommodationPrice = data.TotalAccommodationPrice;
    this.OriginalTotalServicesPrice = data.TotalServicesPrice;
    this.OriginalTotalAccommodationVat = data.TotalAccommodationVat;
    this.OriginalTotalServicesVat = data.TotalServicesVat;

    this.haveReviewedRoom = false;

    this.haveSeenServices = false;
    this.areServicesLoaded = false;
    this.hasSelectableServices = false;

    this.hasUpgrades = false;
    this.haveSeenUpgrades = true;
  }

  ReservationItemId: number;
  roomId: string;
  roomNumber: number;

  haveReviewedRoom: boolean;
  get haveReviewedAllGuests(): boolean {
    return this.Guests.reduce((t, y) => {
      return t && y.haveReviewedGuest && y.isValid;
    }, true);
  }

  DateOfArrival: string;
  DateOfDeparture: string;

  get numberOfNights(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.DateOfArrival,
      this.DateOfDeparture,
    );
    return totalDays;
  }

  RoomTypeId: number;
  RoomType: string;
  RatePlanId: number;
  RatePlan: string;
  Rate: RateDTO; // Only used when selecting a new room to upgrade.

  updatingRates: boolean;
  visitorLevy: VisitorLevyDTO | null;

  Policy?: PolicyDTO;

  RoomConfigurationIsRequired: boolean;
  RoomConfiguration?: ApiDropdown;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  CreditCards: CreditCardDTO[];
  Guests: Guest[];

  // Not required for precheckin
  hasUpgrades: boolean;
  haveSeenUpgrades: boolean;
  // No-op function to match interface.
  checkForUpgrades(): void {
    return;
  }

  get isValid(): boolean {
    return (
      this.guestsAreValid &&
      (!this.RoomConfigurationIsRequired ||
        (this.RoomConfigurationIsRequired && this.RoomConfiguration != null))
    );
  }

  get roomConfigurationIsValid(): boolean {
    return (
      !this.RoomConfigurationIsRequired ||
      (this.RoomConfigurationIsRequired && this.RoomConfiguration != null)
    );
  }

  get guestsAreValid(): boolean {
    const v = this.Guests.reduce((v, g) => {
      return v && g.isValid;
    }, true);
    return v;
  }

  get guestSummary(): string {
    return utils.getGuestSummaryText(
      this.NumberOfAdults,
      this.NumberOfChildren,
      this.NumberOfInfants,
    );
  }

  activeGuestNumber(guest: Guest): number {
    let index = -2;

    index = this.Guests.findIndex((u) => u == guest);
    index = index + 1;

    return index;
  }

  /// Find a reservation item based on Room Id, with an optional selector
  getGuest(
    guestId: string,
    selector: (ri: Guest) => boolean = () => {
      return true;
    },
  ): Guest | null {
    const items = this.Guests.filter((y) => {
      return y.guestId == guestId && selector(y);
    });
    if (items.length > 0) {
      return items[0];
    }
    return null;
  }

  getNextGuest(
    currentGuest: Guest,
    selector: (ri: Guest) => boolean,
  ): Guest | null {
    let nextGuest: Guest = null;
    let index = -1;

    const getNextGuest = (index: number) => {
      let nextX = this.Guests[index];
      if (nextX != null) {
        const resX = this.getGuest(nextX.guestId, selector);
        if (resX == null) {
          nextX = getNextGuest(index + 1);
        } else {
          nextX = resX;
        }
      }

      return nextX;
    };

    if (currentGuest == null) {
      index = 0;
      nextGuest = getNextGuest(index);
    } else {
      index = this.Guests.findIndex((y) => {
        return y.guestId == currentGuest?.guestId;
      });

      if (index > -1) {
        nextGuest = getNextGuest(index + 1);
      }
    }

    return nextGuest;
  }

  Services: ReservationItemService[];

  get hasServices(): boolean {
    return this.Services.length > 0;
  }

  // True if there are services available for selection
  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;

  getService(serviceId: number): ReservationItemService | null {
    const s = this.Services.filter((u) => {
      return u.ServiceId == serviceId;
    });

    return s.length > 0 ? s[0] : null;
  }

  get canEditServices(): boolean {
    let canEdit = false;
    if (this.Services.length == 0) {
      canEdit = true;
    } else {
      canEdit = this.Services.reduce((v, ris) => {
        return v || ris.canEdit || ris.justAdded;
      }, false);
    }

    return canEdit;
  }

  get canShowEditServices(): boolean {
    return (
      this.hasServices || (this.hasSelectableServices && this.canEditServices)
    );
  }

  // Used for services
  get numberOfGuests(): number {
    return this.NumberOfAdults + this.NumberOfChildren;
  }

  OriginalTotalAccommodationPrice: number;
  OriginalTotalServicesPrice: number;
  OriginalTotalAccommodationVat: number;
  OriginalTotalServicesVat: number;

  VatSettings: IVatSettings;

  get TotalServicesPrice(): number {
    const cost = rateHelperUtils.calcuateTotalServiceCost(
      this.Services,
      this.numberOfNights,
      this.numberOfGuests,
    );
    return cost;
  }

  get TotalServicesVat(): number {
    const vatAmount = rateHelperUtils.calculateVatAmount(
      this.TotalServicesPrice,
      this.VatSettings,
    );
    return vatAmount;
  }

  get TotalAccommodationPrice(): number {
    let cost = this.OriginalTotalAccommodationPrice;
    if (this.Rate != null) {
      const rate = new Rate(this.Rate);
      cost = rateHelperUtils.calculateRoomRate(
        this.NumberOfAdults,
        this.NumberOfChildren,
        this.NumberOfInfants,
        rate.BaseRate,
        rate.BaseNumberOfAdults,
        rate.CostPerRoom,
        rate.CostPerAdult,
        rate.CostPerChild,
        rate.CostPerInfant,
        rate.PerPersonCostPerAdult,
        rate.PerPersonCostPerChild,
        rate.PerPersonCostPerInfant,
      );
    }
    return cost;
  }

  get TotalAccommodationVat(): number {
    let vat = this.OriginalTotalAccommodationVat;
    if (this.Rate != null) {
      vat = rateHelperUtils.calculateVatAmount(
        this.TotalAccommodationPrice,
        this.VatSettings,
      );
    }
    return vat;
  }

  toDTO(): PreCheckInReservationItemEditDTO {
    return {
      ReservationItemId: this.ReservationItemId,
      RoomConfigurationDropdownOptionId: this.RoomConfiguration?.Id,
      Guests: this.Guests.map((g) => {
        return g.toDTO();
      }),
      Services: this.Services.map((ris: ReservationItemService) => {
        return {
          ReservationItemId: ris.ReservationItemId,
          ServiceId: ris.ServiceId,
          Quantity: ris.Quantity,
          Service: ris.Service,
        } as PreCheckInReservationItemServiceDTO;
      }),
    } as PreCheckInReservationItemEditDTO;
  }
}
